const normalizePathname = (pathname = '') => {
  const trimmed = String(pathname).split(/[?#]/, 1)[0] || '';
  if (!trimmed || trimmed === '/') {
    return trimmed || '/';
  }
  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
};

export const buildAdminTarget = (domainKey, toolId, extraParams = null, basePath = '/admin') => {
  const params = new URLSearchParams({ tool: toolId });
  for (const [key, value] of Object.entries(extraParams || {})) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, value);
    }
  }
  const normalizedBasePath = String(basePath || '/admin').replace(/\/+$/, '') || '/admin';
  return `${normalizedBasePath}/${domainKey}?${params.toString()}`;
};

const parseAdminTarget = (target = '') => {
  const [pathAndSearch, hashFragment = ''] = String(target).split('#', 2);
  const [pathname = '', searchFragment = ''] = pathAndSearch.split('?', 2);

  return {
    pathname: pathname || '/',
    searchParams: new URLSearchParams(searchFragment),
    hash: hashFragment ? `#${hashFragment}` : '',
  };
};

export const ADMIN_DOMAINS = [
  {
    key: 'review',
    label: 'Review',
    description: 'Feeds to monitor and triage.',
    defaultTool: 'inbox',
    tools: [
      {
        id: 'inbox',
        label: 'Inbox',
        description: 'Unified activity feed for signups, uploads, purchases, and reports.',
        section: 'feeds',
        load: () => import('../../pages/AdminInbox'),
        legacyPaths: ['/admin/inbox', '/admin/feedback/bugs'],
      },
      {
        id: 'vanc-math-curation',
        label: 'Vanc Math Curation',
        description: 'Opens the standalone Vanc math question curation port (edward:8765) in a new tab.',
        section: 'feeds',
        // Tailscale IP — port lives on edward only, not exposed via
        // phillyshell.cloud. Browser MUST be on the tailnet (or local edward)
        // to reach this. Hardcoded because window.location.hostname returns
        // `debono-preprod.phillyshell.cloud` in prod-routed browsing, which
        // doesn't have port 8765.
        external: 'http://100.111.71.48:8765',
      },
      {
        id: 'runtime-status',
        label: 'Runtime Status',
        description: 'Track live runtime state for the full local stack.',
        section: 'health',
        load: () => import('../../pages/AdminRuntimeStatus'),
        legacyPaths: ['/admin/runtime-status'],
        withErrorBoundary: true,
      },
      {
        id: 'inference-health',
        label: 'Inference Health',
        description: 'Inspect local and fallback model availability before failures spread.',
        section: 'health',
        load: () => import('../../pages/AdminInferenceHealth'),
        legacyPaths: ['/admin/inference-health'],
        withErrorBoundary: true,
      },
      {
        id: 'api-logs',
        label: 'API Logs',
        description: 'Review recent API traffic and failure signatures.',
        section: 'logs',
        load: () => import('../../pages/AdminApiLogs'),
        legacyPaths: ['/admin/api-logs'],
        withErrorBoundary: true,
      },
      {
        id: 'audit-trail',
        label: 'Audit Trail',
        description: 'Append-only record of admin actions with actor, target, and IP.',
        section: 'logs',
        load: () => import('../../pages/AdminAuditTrail'),
        legacyPaths: ['/admin/audit-trail'],
        withErrorBoundary: true,
      },
      {
        id: 'user-spend',
        label: 'User Spend',
        description: 'Per-user monthly API token spend against free-tier limits.',
        section: 'signals',
        load: () => import('../../pages/AdminUserSpend'),
        legacyPaths: ['/admin/user-spend'],
        withErrorBoundary: true,
      },
      {
        id: 'slide-gaps',
        label: 'Slide Gaps',
        description: 'Track missing structured schemas and target recovery per deck.',
        section: 'signals',
        load: () => import('../../pages/AdminSlideGaps'),
        legacyPaths: ['/admin/slide-gaps'],
        withErrorBoundary: true,
      },
      {
        id: 'embedder-analytics',
        label: 'Embedder Analytics',
        description: 'Review retired embedder feedback and migration context.',
        section: 'signals',
        load: () => import('../../pages/AdminFeedbackDashboard'),
        legacyPaths: ['/admin/feedback/embedders'],
      },
      {
        id: 'training-corpus',
        label: 'Training Corpus',
        description: 'Review and label LLM call pairs queued for SFT.',
        section: 'queues',
        load: () => import('../../pages/AdminTrainingCorpus'),
        legacyPaths: ['/admin/sandbox/training-corpus'],
        withErrorBoundary: true,
      },
      {
        id: 'dlc-content-review',
        label: 'DLC Content Review',
        description: 'Browse GLM-augmented vs legacy NAPLEX/PTCB content (preprod only).',
        section: 'signals',
        load: () => import('../../pages/AdminDlcContentReview'),
        legacyPaths: [],
        withErrorBoundary: true,
        availability: 'preprod-only',
      },
    ],
  },
  {
    key: 'audit',
    label: 'Audit',
    description: 'Data correctness checks across the pipeline.',
    defaultTool: 'coverage',
    tools: [
      {
        id: 'coverage',
        label: 'Fact Validation',
        description: 'Inspect atom and fact coverage across processed decks.',
        section: 'coverage',
        load: () => import('../../pages/AdminCoverage'),
        legacyPaths: ['/admin/coverage'],
      },
      {
        id: 'slide-coverage',
        label: 'Slide Coverage',
        description: 'Measure how much of each slide survives the pipeline.',
        section: 'coverage',
        load: () => import('../../pages/AdminSlideCoverage'),
        legacyPaths: ['/admin/coverage/slides'],
      },
      {
        id: 'atom-explorer',
        label: 'Atom Explorer',
        description: 'Traverse atom storage and inspect downstream graph links.',
        section: 'coverage',
        load: () => import('../../pages/AdminAtomExplorer'),
        legacyPaths: ['/admin/coverage/atoms'],
      },
      {
        id: 'graph',
        label: 'Graph Browser',
        description: 'Inspect graph relationships without leaving admin.',
        section: 'coverage',
        load: () => import('../../pages/AdminGraph'),
        legacyPaths: ['/admin/graph'],
      },
      {
        id: 'training-data',
        label: 'Training Data',
        description: 'Inspect curated training examples feeding the system.',
        section: 'datasets',
        load: () => import('../../pages/AdminTrainingData'),
        legacyPaths: ['/admin/training-data'],
      },
      {
        id: 'inference-corpus',
        label: 'Inference Corpus',
        description: 'Inspect raw inference logs, prompts, and prompt-version coverage.',
        section: 'datasets',
        load: () => import('../../pages/AdminInferenceCorpus'),
        legacyPaths: ['/admin/inference-corpus'],
        withErrorBoundary: true,
      },
      {
        id: 'contradictions',
        label: 'Contradictions',
        description: 'Resolve verifier disputes from the content-KG queue.',
        section: 'datasets',
        load: () => import('../../pages/admin/Contradictions'),
        legacyPaths: ['/admin/knowledge-quality/contradictions'],
        withErrorBoundary: true,
      },
      {
        id: 'unsupported-drug-facts',
        label: 'Unsupported Drug Facts',
        description: 'Review SLICE-1 drug facts not supported by the authoritative DailyMed section.',
        section: 'datasets',
        load: () => import('../../pages/admin/UnsupportedDrugFacts'),
        legacyPaths: ['/admin/knowledge-quality/unsupported-drug-facts'],
        withErrorBoundary: true,
      },
    ],
  },
  {
    key: 'forge',
    label: 'Forge',
    description: 'Create, upload, and curate authored content.',
    defaultTool: 'upload-cache',
    tools: [
      {
        id: 'upload-cache',
        label: 'Upload Cache',
        description: 'Inspect cached upload payloads and annex or purge them quickly.',
        section: 'intake',
        load: () => import('../../pages/AdminStorageUploads'),
        legacyPaths: ['/admin/storage/uploads'],
      },
      {
        id: 'deck-codes',
        label: 'Deck Codes',
        description: 'Audit deck code assignments and related admin controls.',
        section: 'content',
        load: () => import('../../pages/AdminDeckCodes'),
        legacyPaths: ['/admin/deck-codes'],
      },
      {
        id: 'drug-storage',
        label: 'Drug Cards',
        description: 'Browse stored drug cards and the canonical pharmacology layer.',
        section: 'content',
        load: () => import('../../pages/AdminStorageDrugs'),
        legacyPaths: ['/admin/storage/drugs'],
      },
      {
        id: 'dlc-packs',
        label: 'DLC Packs',
        description: 'Inspect shared DLC concept packs and per-user treadmill progress.',
        section: 'content',
        load: () => import('../../pages/AdminDlcPacks'),
        legacyPaths: [],
      },
      {
        id: 'reservoir',
        label: 'Golden Reservoir',
        description: 'Inspect the full card reservoir and combat effect metadata.',
        section: 'content',
        load: () => import('../../pages/AdminReservoir'),
        legacyPaths: ['/admin/reservoir'],
      },
      {
        id: 'keystone-synergies',
        label: 'Keystone Synergies',
        description: 'Preview keystone synergy definitions against hypothetical user state.',
        section: 'content',
        load: () => import('../../pages/admin/KeystoneSynergyPreviewPage'),
        legacyPaths: ['/admin/keystone-synergies'],
        withErrorBoundary: true,
      },
      {
        id: 'keystone-beats',
        label: 'Keystone Beats',
        description: 'Mass view and edit keystone beats, stratified by keystone.',
        section: 'content',
        load: () => import('../../pages/admin/KeystoneBeatsAuthoring'),
        legacyPaths: [],
        withErrorBoundary: true,
      },
      {
        id: 'skill-tree-authoring',
        label: 'Skill Tree Authoring',
        description: 'Author substrate node positions and labels against the live dashboard renderer.',
        section: 'substrate authoring',
        load: () => import('../../pages/admin/SkillTreeAuthoringTab'),
        legacyPaths: ['/admin/skill-tree-authoring'],
        withErrorBoundary: true,
      },
      {
        id: 'brand-assets',
        label: 'Brand Assets',
        description: 'Inspect brand primitives against the live admin shell.',
        section: 'brand',
        load: () => import('../../pages/AdminBrandAssets'),
        legacyPaths: ['/admin/test/brand-assets'],
      },
      {
        id: 'marketing',
        label: 'Marketing',
        description: 'Preview campaign treatments and marketing-facing mocks.',
        section: 'brand',
        load: () => import('../../pages/AdminMarketingCampaigns'),
        legacyPaths: ['/admin/test/marketing'],
      },
      {
        id: 'concept-cards',
        label: 'Concept Cards',
        description: 'Exercise concept card authoring and display in a local harness.',
        section: 'content',
        load: () => import('../../pages/AdminConceptTest'),
        legacyPaths: ['/admin/test/concept-cards'],
      },
    ],
  },
  {
    key: 'lab',
    label: 'Lab',
    description: 'Experiment with generation and pipeline variants.',
    defaultTool: 'boss-combat',
    tools: [
      {
        id: 'slide-pipeline',
        label: 'Slide Pipeline',
        description: 'Standardization, cloud pipeline, spot checks, proposals, model comparison, and iterations.',
        section: 'slides',
        load: () => import('../../pages/admin/lab/SlidePipelineLab'),
        legacyPaths: [
          '/admin/lab/standardization',
          '/admin/standardize',
          '/admin/lab/spot-check',
          '/admin/lab/proposals',
          '/admin/lab/model-comparison',
          '/admin/lab/slide-iterations',
        ],
        legacyPathTabs: {
          '/admin/lab/standardization': 'standardization',
          '/admin/standardize': 'cloud-pipeline',
          '/admin/lab/spot-check': 'spot-check',
          '/admin/lab/proposals': 'proposals',
          '/admin/lab/model-comparison': 'model-comparison',
          '/admin/lab/slide-iterations': 'iterations',
        },
      },
      {
        id: 'concept-teaching',
        label: 'Concept & Teaching',
        description: 'Teaching comparison, concept card logs, and the LLM route map.',
        section: 'teaching',
        load: () => import('../../pages/admin/lab/ConceptTeachingLab'),
        legacyPaths: [
          '/admin/lab/teaching-compare',
          '/admin/concept-cards',
          '/admin/lab/llm-routes',
        ],
        legacyPathTabs: {
          '/admin/lab/teaching-compare': 'teaching-compare',
          '/admin/concept-cards': 'concept-card-logs',
          '/admin/lab/llm-routes': 'llm-routes',
        },
        legacyToolIds: ['concept-cards'],
        withErrorBoundary: true,
      },
      {
        id: 'boss-combat',
        label: 'Boss & Combat',
        description: 'Boss labs, question inventories, previews, exam lab, algorithms, and ELO ratings.',
        section: 'boss',
        load: () => import('../../pages/admin/lab/BossCombatLab'),
        legacyPaths: [
          '/admin/lab/boss-lab',
          '/admin/lab/boss-questions',
          '/admin/test/boss-preview',
          '/admin/lab/exam',
          '/admin/test/algorithms',
          '/admin/elo',
        ],
        legacyPathTabs: {
          '/admin/lab/boss-lab': 'boss-lab',
          '/admin/lab/boss-questions': 'boss-questions',
          '/admin/test/boss-preview': 'boss-preview',
          '/admin/lab/exam': 'exam-lab',
          '/admin/test/algorithms': 'algorithms',
          '/admin/elo': 'elo-ratings',
        },
        withErrorBoundary: true,
      },
      {
        id: 'question-stats',
        label: 'Question Stats',
        description: 'Per-question combat item analysis — served, %correct, too-easy/too-hard/broken, by cohort.',
        section: 'boss',
        load: () => import('../../pages/admin/AdminQuestionStats'),
        legacyPaths: ['/admin/question-stats'],
        withErrorBoundary: true,
      },
      {
        id: 'cram-map-proposals',
        label: 'CRAM Map Proposals',
        description: 'Canonical CRAM territory preview plus proposal variants A, B, and C.',
        section: 'previews',
        load: () => import('../../pages/admin/lab/CramMapProposalsLab'),
        legacyPaths: [
          '/admin/test/cram-map-proposal',
          '/admin/test/cram-map-proposal-a',
          '/admin/test/cram-map-proposal-b',
          '/admin/test/cram-map-proposal-c',
        ],
        legacyPathTabs: {
          '/admin/test/cram-map-proposal': 'canonical',
          '/admin/test/cram-map-proposal-a': 'proposal-a',
          '/admin/test/cram-map-proposal-b': 'proposal-b',
          '/admin/test/cram-map-proposal-c': 'proposal-c',
        },
      },
      {
        id: 'responsive-viewer',
        label: 'Mobile App',
        description: 'Preview the app as a mobile app at phone viewport sizes.',
        section: 'previews',
        load: () => import('../../pages/admin/responsive-viewer/ResponsiveViewerPage'),
      },
      {
        id: 'multiplayer',
        label: 'Multiplayer',
        description: 'PvP handshake and connectivity sandbox.',
        section: 'previews',
        load: () => import('../../pages/admin/lab/MultiplayerLab'),
        legacyPaths: ['/admin/test/pvp-handshake'],
        legacyPathTabs: {
          '/admin/test/pvp-handshake': 'pvp-handshake',
        },
        beta: true,
      },
    ],
  },
  {
    key: 'ops',
    label: 'Ops',
    description: 'Site settings, users, services, and admin access.',
    defaultTool: 'settings',
    tools: [
      {
        id: 'perpetual-services',
        label: 'Perpetual Services',
        description: 'Watch keep-warm service depth, queue, rate, and health.',
        section: 'services',
        load: () => import('../../pages/AdminPerpetualServices'),
        legacyPaths: ['/admin/lab/perpetual-services', '/admin/ops/perpetual-services'],
        withErrorBoundary: true,
      },
      {
        id: 'observability',
        label: 'Observability',
        description: 'Generation provenance, decision events, paid-API spend ledgers, and schema-migration state.',
        section: 'services',
        load: () => import('../../pages/AdminObservability'),
        legacyPaths: ['/admin/ops/observability'],
        withErrorBoundary: true,
      },
      {
        id: 'canonical-sources',
        label: 'Canonical Sources',
        description: 'SageRx pipeline ops: upstream freshness, entity table drift, NER tagging coverage, retro-tag jobs, contradictions link-out.',
        section: 'services',
        load: () => import('../../pages/admin/CanonicalSources'),
        legacyPaths: [
          '/admin/knowledge-quality/canonical-sources',
          '/admin/knowledge-quality?tool=canonical-sources',
        ],
        withErrorBoundary: true,
      },
      {
        id: 'settings',
        label: 'Settings',
        description: 'Toggle cloud standardization and related runtime switches.',
        section: 'config',
        load: () => import('../../pages/AdminSettings'),
        legacyPaths: ['/admin/settings'],
      },
      {
        id: 'users',
        label: 'Users',
        description: 'Inspect user records and admin-side account controls.',
        section: 'config',
        load: () => import('../../pages/AdminUsers'),
        legacyPaths: ['/admin/users'],
      },
      {
        id: 'panel',
        label: 'Test Accounts',
        description: 'Launch panel tooling and impersonation workflows.',
        section: 'config',
        load: () => import('../../pages/AdminPanel'),
        legacyPaths: ['/admin/panel'],
      },
    ],
  },
];

const PARTNER_ADMIN_TOOLS = new Set([
  'inbox',
  'runtime-status',
  'inference-health',
  'api-logs',
  'audit-trail',
  'user-spend',
  'slide-gaps',
  'coverage',
  'slide-coverage',
  'atom-explorer',
  'graph',
  'upload-cache',
  'deck-codes',
  'drug-storage',
  'dlc-packs',
  'reservoir',
  'brand-assets',
  'marketing',
  'concept-teaching',
  'boss-combat',
  'cram-map-proposals',
  'responsive-viewer',
  'multiplayer',
  'perpetual-services',
  'settings',
]);

for (const domain of ADMIN_DOMAINS) {
  for (const tool of domain.tools) {
    tool.requires = PARTNER_ADMIN_TOOLS.has(tool.id) ? ['admin', 'partner_admin'] : ['admin'];
  }
}

const ADMIN_DOMAIN_MAP = new Map(ADMIN_DOMAINS.map((domain) => [domain.key, domain]));
const ADMIN_TOOL_MAP = new Map();
const LEGACY_ADMIN_TARGETS = new Map();

for (const domain of ADMIN_DOMAINS) {
  const toolMap = new Map();
  for (const tool of domain.tools) {
    toolMap.set(tool.id, tool);
    for (const legacyToolId of tool.legacyToolIds || []) {
      toolMap.set(legacyToolId, tool);
    }
    for (const legacyPath of tool.legacyPaths || []) {
      const normalizedLegacyPath = normalizePathname(legacyPath);
      if (LEGACY_ADMIN_TARGETS.has(normalizedLegacyPath)) {
        throw new Error(`Duplicate legacy admin path detected: ${normalizedLegacyPath}`);
      }
      LEGACY_ADMIN_TARGETS.set(normalizedLegacyPath, buildAdminTarget(domain.key, tool.id, {
        tab: tool.legacyPathTabs?.[legacyPath] || tool.legacyPathTabs?.[normalizedLegacyPath],
        asset: tool.legacyPathAssets?.[legacyPath] || tool.legacyPathAssets?.[normalizedLegacyPath],
      }));
    }
  }
  // External-only domains (no tools, open in new tab) skip the defaultTool
  // validation — they never mount an internal panel.
  if (!domain.external && !toolMap.has(domain.defaultTool)) {
    throw new Error(`Admin domain "${domain.key}" is missing default tool "${domain.defaultTool}"`);
  }
  ADMIN_TOOL_MAP.set(domain.key, toolMap);
}

export function getAdminDomain(domainKey) {
  return ADMIN_DOMAIN_MAP.get(domainKey) || null;
}

export function roleCanAccessTool(tool, role = 'admin') {
  const allowed = tool?.requires || ['admin'];
  return allowed.includes(role);
}

export function toolIsAvailable(tool, nodeRole = null) {
  const availability = tool?.availability || 'any';
  if (availability === 'any') {
    return true;
  }
  if (availability === 'preprod-only') {
    return nodeRole === 'preprod';
  }
  return true;
}

export function filterAdminDomainsByRole(role = 'admin', nodeRole = null) {
  return ADMIN_DOMAINS
    .map((domain) => {
      // External-only domains have no internal tools but should still appear
      // in the workspace nav (the rendering layer handles them as new-tab links).
      if (domain.external) {
        return { ...domain, tools: [] };
      }
      const tools = domain.tools.filter((tool) => (
        roleCanAccessTool(tool, role) && toolIsAvailable(tool, nodeRole)
      ));
      if (!tools.length) {
        return null;
      }
      return {
        ...domain,
        tools,
        defaultTool: tools.some((tool) => tool.id === domain.defaultTool) ? domain.defaultTool : tools[0].id,
      };
    })
    .filter(Boolean);
}

export function applyAdminLayoutToDomains(domains, layout = {}) {
  const hidden = new Set(layout.hidden || []);
  const pinned = new Set(layout.pinned || []);
  const order = new Map((layout.tool_order || []).map((id, index) => [id, index]));

  return domains
    .map((domain) => {
      const tools = domain.tools
        .filter((tool) => !hidden.has(tool.id))
        .slice()
        .sort((left, right) => {
          const leftPinned = pinned.has(left.id) ? 0 : 1;
          const rightPinned = pinned.has(right.id) ? 0 : 1;
          if (leftPinned !== rightPinned) return leftPinned - rightPinned;
          const leftOrder = order.has(left.id) ? order.get(left.id) : Number.MAX_SAFE_INTEGER;
          const rightOrder = order.has(right.id) ? order.get(right.id) : Number.MAX_SAFE_INTEGER;
          if (leftOrder !== rightOrder) return leftOrder - rightOrder;
          return 0;
        });
      if (!tools.length) return null;
      return {
        ...domain,
        tools,
        defaultTool: tools.some((tool) => tool.id === domain.defaultTool) ? domain.defaultTool : tools[0].id,
      };
    })
    .filter(Boolean);
}

export function getAdminTool(domainKey, toolId, role = 'admin', nodeRole = null) {
  const toolMap = ADMIN_TOOL_MAP.get(domainKey);
  if (!toolMap || !toolId) {
    return null;
  }
  const tool = toolMap.get(toolId) || null;
  return roleCanAccessTool(tool, role) && toolIsAvailable(tool, nodeRole) ? tool : null;
}

export function getAdminToolTarget(domainKey, toolId, role = 'admin', nodeRole = null) {
  const tool = getAdminTool(domainKey, toolId, role, nodeRole);
  if (!tool) {
    return null;
  }
  return buildAdminTarget(domainKey, tool.id);
}

export function getDefaultAdminTarget(domainKey, role = 'admin', nodeRole = null) {
  const domain = filterAdminDomainsByRole(role, nodeRole).find((item) => item.key === domainKey);
  if (!domain) {
    return null;
  }
  return buildAdminTarget(domain.key, domain.defaultTool);
}

export function getLegacyAdminTarget(pathname) {
  return LEGACY_ADMIN_TARGETS.get(normalizePathname(pathname)) || null;
}

export function mergeAdminTarget(target, currentSearch = '', currentHash = '') {
  const { pathname, searchParams: targetSearchParams, hash: targetHash } = parseAdminTarget(target);
  const mergedParams = new URLSearchParams(currentSearch);

  for (const [key, value] of targetSearchParams.entries()) {
    mergedParams.set(key, value);
  }

  const search = mergedParams.toString();
  const hash = targetHash || currentHash || '';

  return `${pathname}${search ? `?${search}` : ''}${hash}`;
}

export function findAdminDomainByPath(pathname) {
  const normalizedPath = normalizePathname(pathname);

  for (const domain of ADMIN_DOMAINS) {
    if (normalizedPath === `/admin/${domain.key}`) {
      return domain;
    }
  }

  const legacyTarget = getLegacyAdminTarget(normalizedPath);
  if (!legacyTarget) {
    return null;
  }

  const targetPath = legacyTarget.split('?', 1)[0];
  return ADMIN_DOMAINS.find((domain) => targetPath === `/admin/${domain.key}`) || null;
}
